import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { AddedToCart } from './AddedToCart.js';
import { giveToSchool } from './giveToSchool.js';
import { Svg } from './Svg.js';

/**
 * A give widget (that is, a form with an amount-input and a 'give' submit button) that will, on submit, add a
 * 'school-level giving' donation to the specified school to your cart.
 */
// ignore unused exports DEFAULT_LAST_GIVE_BUTTON_AMOUNT
export const DEFAULT_LAST_GIVE_BUTTON_AMOUNT = 500;

export const SchoolGiveWidget = ({ schoolId, schoolName, useAjax, totalCostToComplete, onSupportAnotherClassroom }) => {
    const [amount, setAmount] = useState(0);
    const [requestComplete, setRequestComplete] = useState(false);
    const [isGiveButtonToggled, setIsGiveButtonToggled] = useState(false);
    const [isCustomAmountOpen, setIsCustomGiveAmountOpen] = useState(false);
    const [customAmount, setCustomGiveAmount] = useState('');
    const smallGiveButtonAmount = 50;
    const mediumGiveButtonAmount = 100;
    const largeGiveButtonAmount = 250;
    const finalGiveButtonAmount = totalCostToComplete && totalCostToComplete > DEFAULT_LAST_GIVE_BUTTON_AMOUNT
        ? totalCostToComplete
        : DEFAULT_LAST_GIVE_BUTTON_AMOUNT;
    const finalGiveButtonDisplayAmount = totalCostToComplete && totalCostToComplete > DEFAULT_LAST_GIVE_BUTTON_AMOUNT
        ? Math.round(totalCostToComplete)
        : DEFAULT_LAST_GIVE_BUTTON_AMOUNT;

    const handleGiveAmount = (e, newAmount) => {
        setAmount(newAmount);
        if (useAjax) {
            e.preventDefault();
            giveToSchool(schoolId, newAmount, onSuccess, onFailure);
        }
    };

    const handleCustomAmount = (e, newAmount) => {
        if (!customAmount) {
            return;
        }
        handleGiveAmount(e, newAmount);
    };

    const handleSubmit = (e) => {
        if (useAjax) {
            e.preventDefault();
            giveToSchool(schoolId, newAmount, onSuccess, onFailure);
        }
    };

    const onSuccess = () => {
        setRequestComplete(true);
        setCustomGiveAmount('');
        setIsCustomGiveAmountOpen(false);
        setIsGiveButtonToggled(pageName == 'school_info');
    };
    const onFailure = (data) => {
        // SLG2TODO Consult Product: what should we do when this fails?
        if (data.responseText === 'amount') {
            alert('Please enter at least $1.');
        } else {
            alert('Something went wrong!');
        }
    };

    // On mount, munge last-search cookies as if the user had just searched for this school.
    // We use that last-search cookie to update the user's favorites when they make their donation.
    useEffect(() => {
        // School-search-param is 'school', not 'schoolid'
        const schoolSearchUrl = `${baseURL}donors/search.html?school=${schoolId}`;
        createCookie('searchTermsURL', schoolSearchUrl, 1);
        createCookie('searchTermsDesc', window.b64EncodeUnicode(schoolName), 1);
    }, []);

    const getGiveSection = function () {
        let giveSection;
        if (!isGiveButtonToggled) {
            giveSection =
                <button className='button-primary' onClick={() => setIsGiveButtonToggled(!isGiveButtonToggled)} onSubmit={(e) => { e.preventDefault(); }} tabIndex='0'>
                    Give to this school
                </button>;
        } else if (!isCustomAmountOpen) {
            giveSection =
                <div className='suggested-give-amounts'>
                    <div className='anchored-amounts'>
                        <button className='button-primary suggested-amount' onClick={(e) => { handleGiveAmount(e, smallGiveButtonAmount); }} tabIndex='0'>${smallGiveButtonAmount}</button>
                        <button className='button-primary suggested-amount' onClick={(e) => { handleGiveAmount(e, mediumGiveButtonAmount); }} tabIndex='0'>${mediumGiveButtonAmount}</button>
                        <button className='button-primary suggested-amount' onClick={(e) => { handleGiveAmount(e, largeGiveButtonAmount); }} tabIndex='0'>${largeGiveButtonAmount}</button>
                        <button className={'button-primary suggested-amount' + (totalCostToComplete && totalCostToComplete > DEFAULT_LAST_GIVE_BUTTON_AMOUNT ? ' complete' : '')}
                            onClick={(e) => { handleGiveAmount(e, finalGiveButtonAmount); }} tabIndex='0'>${finalGiveButtonDisplayAmount}</button>
                    </div>
                    <div className='custom'>
                        <button className='text-link custom-amount' onClick={() => setIsCustomGiveAmountOpen(!isCustomAmountOpen)} tabIndex='0'>Give another amount</button>
                    </div>
                </div>
            ;
        } else {
            giveSection =
               <div className='custom input-holder dollar-amount'>
                   <input name='value' type='number' className='' placeholder='' autoFocus value={customAmount} onChange={(e) => setCustomGiveAmount(e.target.value)} />
                   <input name='schoolId' type='hidden' value={schoolId} />
                   <div className='inline-button'><button onClick={(e) => handleCustomAmount(e, customAmount)} className='button-primary small'>Give</button></div>
               </div>;
        }

        return (
            <div className='give-container'>
                {giveSection}
                <div className="tooltip-wrapper disclaimer full-width subtle-link" tabIndex="0" aria-label="tooltip">
                    How is my gift used?
                    <span className="hover-tooltip">
                            Your donation will be applied to a classroom project, if available. If no projects are listed at this school, we'll send the funds to a teacher for later use.
                        <a className="text-link" href="https://help.donorschoose.org/hc/en-us/articles/22411454874775">Learn more</a>
                    </span>
                </div>
            </div>
        );
    };

    if (requestComplete) {
        const combinedOnCloseHandler = () => {
            setRequestComplete(false);
            if (onSupportAnotherClassroom) {
                onSupportAnotherClassroom();
            }
        };

        return <AddedToCart
            amount={amount} //
            onClose={combinedOnCloseHandler}
        />;
    }

    if (useAjax) {
        return (
            <form className='form-redux'>
                {getGiveSection()}
            </form>
        );
    }

    return (
        <form className="school-give-section form-redux" onSubmit={handleSubmit} method={'post'} action={`${secureBaseURL}donors/cart/school-donation`}>
            <input name="schoolId" type="hidden" value={schoolId} />
            <input name="value" type="hidden" value={amount} onChange={(e) => setAmount(e.target.value)} />
            {getGiveSection()}
        </form>
    );
};
SchoolGiveWidget.propTypes = {
    schoolId: PropTypes.string.isRequired,
    schoolName: PropTypes.string.isRequired,
    useAjax: PropTypes.bool,
    totalCostToComplete: PropTypes.number,
    onSupportAnotherClassroom: PropTypes.func,
};
